import React from 'react';
import { Fuel, AlertTriangle, Snowflake, Flame } from 'lucide-react';
import '../styles/enterprise.css';
import '../styles/responsive/pages/Treasury.css';
import { useTheme } from '../context/ThemeContext';
import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import StatusBadge from '../components/common/StatusBadge';
import { TableSkeleton } from '../components/common/Skeleton';
import { useGetTreasuryQuery } from '../store/api/adminOpsApi';
import { formatUSD, formatCrypto, formatCount } from '../utils/formatAmount';

const Treasury = () => {
  const { mode } = useTheme();
  const { data, isLoading, isFetching, refetch } = useGetTreasuryQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const chains = data?.chains || [];
  const totals = data?.totals || {};
  const gasTank = data?.gasTank || {};
  const lowGas = (gasTank.networks || []).filter((n) => n.low === true);

  return (
    <div className="ent ent-page treasury-page">
      <PageHeaderPro
        title="Treasury"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={isFetching ? 'Updating…' : 'Updated just now'}
        actions={
          <button className="ent-btn ent-btn-secondary" onClick={() => refetch()}>
            Refresh
          </button>
        }
      />

      {gasTank.configured && lowGas.length > 0 && (
        <div className="treasury-banner warn">
          <AlertTriangle size={18} />
          <div>
            <strong>Low gas tank</strong>
            <span>
              {' '}Insufficient native balance to cover fees on:{' '}
              {lowGas
                .map((n) => `${n.name} (${formatCrypto(parseFloat(n.balance || 0))} ${n.nativeCurrency})`)
                .join(', ')}
              . Top up before approving payouts on these networks.
            </span>
          </div>
        </div>
      )}

      <KpiStrip>
        <KpiCard
          label="HOT WALLETS (USD)"
          value={`$${formatUSD(totals.hotUsd || 0)}`}
          sub={`${formatCount(chains.length)} chains`}
        />
        <KpiCard label="COLD STORAGE (USD)" value={`$${formatUSD(totals.coldUsd || 0)}`} />
        <KpiCard label="TOTAL HOLDINGS (USD)" value={`$${formatUSD(totals.totalUsd || 0)}`} />
        <KpiCard
          label="GAS TANK"
          value={gasTank.configured ? `${formatCount((gasTank.networks || []).filter((n) => !n.low).length)} OK` : '—'}
          sub={gasTank.configured ? `${formatCount(lowGas.length)} low` : 'Not configured'}
        />
      </KpiStrip>

      <div className="pro-table-wrap">
        {isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : chains.length === 0 ? (
          <div className="empty-state">
            <Fuel size={48} />
            <p>No treasury data</p>
            <span>Per-chain hot/cold balances will appear here</span>
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>CHAIN</th>
                <th className="amount-cell"><Flame size={12} /> HOT</th>
                <th className="amount-cell"><Snowflake size={12} /> COLD</th>
                <th className="amount-cell">GAS (NATIVE)</th>
                <th className="amount-cell">USD VALUE</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {chains.map((c) => (
                <tr key={c.networkId || c.chain}>
                  <td data-label="CHAIN">
                    <div className="cell-2">
                      <span className="cell-2__main">{c.chain || c.name}</span>
                      <span className="cell-2__sub">{c.nativeCurrency}</span>
                    </div>
                  </td>
                  <td className="amount-cell" data-label="HOT">
                    <span className="amount-cell__main mono">{formatCrypto(parseFloat(c.hot || 0))}</span>
                  </td>
                  <td className="amount-cell" data-label="COLD">
                    <span className="amount-cell__main mono">{formatCrypto(parseFloat(c.cold || 0))}</span>
                  </td>
                  <td className="amount-cell" data-label="GAS">
                    <span className="amount-cell__main mono">{formatCrypto(parseFloat(c.gas || 0))}</span>
                  </td>
                  <td className="amount-cell" data-label="USD VALUE">
                    <span className="amount-cell__main mono">${formatUSD(c.usd || 0)}</span>
                  </td>
                  <td data-label="STATUS">
                    <StatusBadge
                      status={c.gasLow ? 'failed' : 'active'}
                      label={c.gasLow ? 'Low gas' : 'Funded'}
                      dot
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Treasury;
